import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  TableContainer,
  Paper,
  Box,
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import '../../styles/UserStyles.css';
import {
  ADD_STAFF,
  EDIT_STAFF,
  VIEW_STAFF,
} from '../../component/common/Routes/AppRoutes';
import { columnFetcher, formatDateWithAmPm, getStatusClass } from '../../Style';
import SearchAndButtonTab from '../../component/ui/SearchAndButtonTab';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetStaffDetail,
  staffDeleteRequest,
  staffFetchAllRequest,
  staffViewAllRequest,
} from '../../redux/Staff/StaffAction';
import { fetchRolesRequest } from '../../redux/Roles/actions/RolesActions';
import { toast, ToastContainer } from 'react-toastify';
import CustomButton from '../../component/ui/FormFields/Button';
import Loading from '../../component/ui/Loading';

const StaffManagementList = () => {
  const user = useSelector((state) => state.auth.user);
  const permissions = user?.permissions;
  const navigate = useNavigate();
  const tableHeaders = [
    'Serial No',
    'Full Name',
    'Email',
    'Mobile',
    'Gender',
    'DOB',
    'Role',
    'Last Active',
    'Created Date',
    'Status',
    'Actions',
  ];
  const columns = [
    { value: 'staff_serial_no', label: 'Serial No' },
    { value: 'full_name', label: 'Full Name' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Mobile' },
    { value: 'gender', label: 'Gender' },
    { value: 'role', label: 'Role' },
    // { value: "status", label: "Status" }
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [searchColumn, setSearchColumn] = useState('staff_serial_no');
  const [open, setOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [page, setPage] = useState(1);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(resetStaffDetail());
  }, []);
  const rowsPerPage = 5;

  const staffsDetails = useSelector(
    (state) => state.staffs?.StaffsDetails || {}
  );

  const viewListLoading = staffsDetails?.loading;
  const staffDeleteDetails = useSelector((state) => state.staffs?.Staff || {});
  const deleteLoading = staffDeleteDetails?.deleteLoading;

  const staffsValue = staffsDetails?.data;
  const totalRecords = staffsDetails?.totalRecords;
  const deleteSuccessMessage = staffDeleteDetails?.success;

  useEffect(() => {
    dispatch(staffFetchAllRequest());
  }, [dispatch]);
  const roles = useSelector((state) => state.roles?.roles || {});

  useEffect(() => {
    dispatch(fetchRolesRequest());
  }, []);

  useEffect(() => {
    if (deleteSuccessMessage) {
      toast.success('Staff Deleted Successfully', {
        position: 'top-center',
        autoClose: 3000,
        onClose: () => {
          dispatch(resetStaffDetail());
          dispatch(staffFetchAllRequest());
        },
      });
    }
  }, [deleteSuccessMessage]);

  const handlePageChange = (event, newPage) => setPage(newPage);

  const handleExport = () => {
    const csvHeaders = [
      'Staff No,Full Name,Email,Mobile,Gender,Role,Address1,Address2,City,State,Country,Last Active,Created Date,Status',
    ];

    const csvRows = staffsValue.map((user) => {
      // Function to properly escape CSV fields
      const escapeCsvField = (field) => {
        if (field === null || field === undefined) return '""';
        const stringField = String(field || '');
        // If the field contains commas, quotes, or newlines, wrap it in quotes and escape any quotes
        if (
          stringField.includes(',') ||
          stringField.includes('"') ||
          stringField.includes('\n')
        ) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      const gender =
        user.gender_type === 'M'
          ? 'Male'
          : user.gender_type === 'F'
          ? 'Female'
          : 'Other';

      const roleName = roleNameFinder(user.role_id);
      const status = user.status == '1' ? 'Active' : 'Inactive';

      return [
        escapeCsvField(user.staff_serial_no),
        escapeCsvField(`${user.first_name} ${user.last_name}`),
        escapeCsvField(user.email),
        escapeCsvField(user.phone),
        escapeCsvField(gender),
        escapeCsvField(roleName),
        escapeCsvField(user.address1),
        escapeCsvField(user.address2),
        escapeCsvField(user.city_name),
        escapeCsvField(user.state_name),
        escapeCsvField(user.country_name),
        escapeCsvField(user.created_at),
        escapeCsvField(user.created_at),
        escapeCsvField(status),
      ].join(',');
    });

    const csvContent = [csvHeaders, ...csvRows].join('\n');
    saveAs(
      new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }),
      'staffs.csv'
    );
  };

  const handleOpenPopup = (user) => {
    setUserToDelete(user);
    setOpen(true);
  };

  const handleClosePopup = () => {
    dispatch(staffFetchAllRequest());
    setOpen(false);
  };
  const roleNameFinder = (id) => {
    const role = roles.find((role) => role.id === id);
    return role ? role.name : 'Unknown Role';
  };
  const filteredStaffs =
    staffsValue?.filter((user) => {
      if (!searchQuery) return true;
      const searchTerm = searchQuery.toLowerCase();
      switch (searchColumn) {
        case 'staff_serial_no':
          return user.staff_serial_no
            ?.toString()
            .toLowerCase()
            .includes(searchTerm);
        case 'full_name':
          return `${user.first_name} ${user.last_name}`
            .toLowerCase()
            .includes(searchTerm);
        case 'email':
          return user.email?.toLowerCase().includes(searchTerm);
        case 'phone':
          return user.phone?.toString().toLowerCase().includes(searchTerm);
        case 'gender':
          const gender =
            user.gender_type === 'M'
              ? 'male'
              : user.gender_type === 'F'
              ? 'female'
              : 'other';
          return gender.includes(searchTerm);
        case 'role':
          return roleNameFinder(user.role_id)
            .toLowerCase()
            .includes(searchTerm);
        case 'status':
          const status = user.status === 1 ? 'active' : 'inactive';
          return status.includes(searchTerm);
        default:
          return true;
      }
    }) || [];

  const paginatedData = useMemo(() => {
    return filteredStaffs.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  }, [filteredStaffs, page, rowsPerPage]);
  const handleConfirmDelete = async (deleteValue) => {
    await dispatch(staffDeleteRequest(deleteValue.id));

    handleClosePopup();
  };
  const renderActions = (userItem) => (
    <div className='button-list'>
      {(user?.user_type === 1 || permissions?.staffs?.view) && (
        <Button
          variant='contained'
          size='small'
          className='view-user-button'
          onClick={() =>
            navigate(`${VIEW_STAFF.replace(':staffId', userItem.id)}`)
          }
        >
          VIEW
        </Button>
      )}
      {(user?.user_type === 1 || permissions?.staffs?.edit) && (
        <Button
          variant='contained'
          size='small'
          className='edit-user-button'
          onClick={() =>
            navigate(`${EDIT_STAFF.replace(':staffId', userItem.id)}`)
          }
        >
          EDIT
        </Button>
      )}
      {(user?.user_type === 1 || permissions?.staffs?.delete) && (
        <CustomButton
          variant='contained'
          className='delete-user-button'
          type='button'
          text={'DELETE'}
          size='small'
          onClick={() => handleOpenPopup(userItem)}
        />
      )}
    </div>
  );

  if (viewListLoading) {
    return <Loading text='Loading Staff details...' />;
  }

  return (
    <>
      <ToastContainer />
      <Box>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <SearchAndButtonTab
                  title='Staffs'
                  columns={columns}
                  searchColumn={searchColumn}
                  setSearchColumn={setSearchColumn}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  handleExport={handleExport}
                  routeName={ADD_STAFF}
                  permissions={permissions}
                  user={user}
                  type='staffs'
                />
                <TableContainer component={Paper} sx={{ marginTop: '16px' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {tableHeaders.map((header) => (
                          <TableCell key={header}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.length > 0 ? (
                        paginatedData.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.staff_serial_no}</TableCell>
                            <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.phone}</TableCell>
                            <TableCell>
                              {user.gender_type === 'M'
                                ? 'Male'
                                : user.gender_type === 'F'
                                ? 'Female'
                                : 'Other'}
                            </TableCell>
                            <TableCell>{user.dob}</TableCell>
                            <TableCell>
                              <span
                                className={`badge ${getStatusClass(
                                  roleNameFinder(user.role_id)
                                )}`}
                              >
                                {roleNameFinder(user.role_id)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {formatDateWithAmPm(user.last_active)}
                            </TableCell>
                            <TableCell>
                              {formatDateWithAmPm(user.created_at)}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`badge ${getStatusClass(
                                  user.status === 1 ? 'Active' : 'Inactive'
                                )}`}
                              >
                                {user.status === 1 ? 'Active' : 'Inactive'}
                              </span>
                            </TableCell>
                            <TableCell>{renderActions(user)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={tableHeaders.length}
                            align='center'
                          >
                            No records found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box className='button-box'>
                  <Pagination
                    count={Math.ceil(filteredStaffs.length / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color='primary'
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Dialog open={open} onClose={handleClosePopup}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{' '}
              <strong>
                {userToDelete?.first_name} {userToDelete?.last_name}
              </strong>
              ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClosePopup}
              variant='contained'
              className='grey-button'
            >
              Cancel
            </Button>
            <CustomButton
              variant='contained'
              className='orange-button'
              type='button'
              loading={deleteLoading}
              text={'DELETE'}
              onClick={() => handleConfirmDelete(userToDelete)}
            />
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default StaffManagementList;
